import { Component } from '@angular/core';
import { map, Observable, of, from, filter } from 'rxjs';
import { SmsRegistrationService } from '../service/sms.service';
import { ActivatedRoute, Router } from '@angular/router';
import { LearnMoreModalComponent } from '../info-banner/learn-more-modal/learn-more-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { RegistrationContent, RegistrationStage, RegistrationTitle } from '../sms.constants';
import { ParticipantType, SmsService } from '@vendasta/smsv2';
import { Environment, EnvironmentService } from '@galaxy/core';
import { switchMap, catchError, shareReplay } from 'rxjs/operators';

type SmsRegistrationCardData = {
  statusBadgeText: 'Setup required' | 'Rejected' | 'In progress' | 'Active';
  statusBadgeColor: 'red' | 'blue' | 'green';
  buttonText: 'Register now' | 'Business info' | 'Update business info' | '';
  buttonType: 'primary' | 'basic' | '';
  title: RegistrationTitle.ApprovedStatus | RegistrationTitle.NotApproved;
  content: RegistrationContent.ApprovedStatus | RegistrationContent.NotApproved;
};

type SMSNumberCardData = {
  statusBadgeText: 'Active';
  statusBadgeColor: 'green';
};

const RegistrationStageToCardData: Record<RegistrationStage, SmsRegistrationCardData> = {
  [RegistrationStage.NotSubmitted]: {
    statusBadgeText: 'Setup required',
    statusBadgeColor: 'red',
    buttonText: 'Register now',
    buttonType: 'primary',
    title: RegistrationTitle.NotApproved,
    content: RegistrationContent.NotApproved,
  },
  [RegistrationStage.ReviewingInfo]: {
    statusBadgeText: 'In progress',
    statusBadgeColor: 'blue',
    buttonText: 'Business info',
    buttonType: 'basic',
    title: RegistrationTitle.NotApproved,
    content: RegistrationContent.NotApproved,
  },
  [RegistrationStage.DataError]: {
    statusBadgeText: 'Rejected',
    statusBadgeColor: 'red',
    buttonText: 'Update business info',
    buttonType: 'primary',
    title: RegistrationTitle.NotApproved,
    content: RegistrationContent.NotApproved,
  },
  // message campaign registration approved
  [RegistrationStage.RegistrationComplete]: {
    statusBadgeText: 'Active',
    statusBadgeColor: 'green',
    buttonText: '',
    buttonType: '',
    title: RegistrationTitle.ApprovedStatus,
    content: RegistrationContent.ApprovedStatus,
  },
};

const SMSNumberToCardData: SMSNumberCardData = {
  statusBadgeText: 'Active',
  statusBadgeColor: 'green',
};

@Component({
  selector: 'sms-registration-card',
  templateUrl: './sms-registration-card.component.html',
  styleUrls: ['./sms-registration-card.component.scss'],
})
export class SmsRegistrationCardComponent {
  constructor(
    private smsRegistrationService: SmsRegistrationService,
    private dialog: MatDialog,
    private router: Router,
    private route: ActivatedRoute,
    private smsService: SmsService,
    private environmentService: EnvironmentService,
  ) {}

  smsNumber$: Observable<string | null> = this.smsRegistrationService.ownerId$.pipe(
    switchMap((internalId) => {
      return from(
        this.smsService.getAccountInfo({
          internalId,
          type: ParticipantType.PARTICIPANT_TYPE_ACCOUNT_GROUP,
        }),
      );
    }),
    catchError((error) => {
      console.log('Error while getting SMS number:', error);
      return of(null);
    }),
    map((res) => res?.phoneNumber || null),
    filter((phoneNumber) => phoneNumber !== null),
  );

  accountGroupInfo$ = this.smsRegistrationService
    .getAccountGroupInfo$()
    .pipe(shareReplay({ bufferSize: 1, refCount: false }));

  country$: Observable<string> = this.accountGroupInfo$.pipe(map((info) => info.napData.country));

  smsCardData$: Observable<SMSNumberCardData | SmsRegistrationCardData> = this.country$.pipe(
    switchMap((country) => {
      if (country === 'CA') {
        return of(SMSNumberToCardData);
      }
      return this.smsRegistrationService.registrationStage$.pipe(map((stage) => RegistrationStageToCardData[stage]));
    }),
  );

  showPopover = false;

  openLearnMoreModal(): void {
    this.dialog.open(LearnMoreModalComponent, { width: '600px' });
  }

  closeInfoPopover(): void {
    this.showPopover = false;
  }

  toggleInfoPopover(): void {
    this.showPopover = !this.showPopover;
  }

  redirectToSMSRegistration(): void {
    this.router.navigate(['./sms-registration'], { relativeTo: this.route });
  }

  getLogoImage(): string {
    const env = this.environmentService.getEnvironment();
    if (env === Environment.PROD) {
      return 'https://vbc-prod.appspot.com/static/images/file-upload.png';
    }
    return 'https://vbc-demo.appspot.com/static/images/file-upload.png';
  }
}
